import { useTheme } from "@mui/material/styles";
import {
  AUCTION_REQUEST_ATTRIBUTED,
  AUCTION_REQUEST_ORIGIN,
  EXTENSION_CONNEXT,
  EXTENSIONS,
  INTEGRATION_EVENT_TYPES,
  PROVIDER_SEARCH_BAVARIA,
  PROVIDER_SEARCH_WESER_EMS,
} from "core/consts";
import { getName } from "core/model/accounts";
import { isCareSearch } from "core/model/auctions";
import { getRequestOrigin } from "core/model/requests";
import { formatPhoneNumber } from "core/model/utils/contacts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { concatStrings } from "core/model/utils/strings";
import { isPrintMode } from "core/model/utils/urls";
import {
  AdmissionsProfile,
  Auction,
  AuctionRequest,
  AuctionRequestOrigin,
  Careseeker as CareseekerType,
  ProviderSearch,
  SocialWorker as SocialWorkerType,
  Station as StationType,
} from "core/types";
import {
  Card,
  CardContentContainer,
  PaperHeader,
} from "ds_legacy/components/InfoCard";
import {
  BLACK,
  BRAND_PRIMARY_MAIN_COLOR,
  WHITE,
} from "ds_legacy/materials/colors";
import {
  Divider,
  HorizontalLayout,
  ResponsiveLayout,
  VerticalLayout,
} from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_14,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  Link,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { Banner } from "ds/ui";
import { useEncryption } from "dsl/atoms/Contexts";
import { useHasCareproviderExtension } from "dsl/hooks/useGetProviderExtensions";
import { PreferredProviderBanner } from "dsl/molecules/Messenger/Forms/shared";
import { AuctionRequestAssigneePanel } from "dsl/molecules/ReceiverAssignee";
import {
  BodyWrapper,
  CategoryContainer,
  EmptiableField,
  EncryptedField,
  getAdmissionsMainContactValue,
  StringField,
  StringValue,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import { TransportInfoCard } from "dsl/organisms/PatientProfileDetailsShared/TransportInfoCard";
import { StaticChipInfo } from "dsl/static/StaticChipInfo";
import { CircleArrowRightIcon } from "lucide-react";
import { ReactNode } from "react";
import { useLoggedCareprovider } from "reduxentities/hooks";
import { useLocale, useTranslations } from "translations";
import Translations from "translations/types";

const RelationshipDisplay = ({
  admissionsProfile,
}: {
  admissionsProfile: AdmissionsProfile | undefined;
}) => {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const { isSealdOnly, withEncryptionAccess } = useEncryption();

  if (!admissionsProfile) {
    return null;
  }

  if (admissionsProfile.relationship_other?.decrypted) {
    return (
      <EncryptedField
        isBold
        isSealdOnly={isSealdOnly}
        prefix={
          translations.careproviderApp.recareAdmissionsForm
            .contactPersonInformation.sectionRelationship
        }
        testId="admissions_relationship_other"
        value={admissionsProfile.relationship_other}
        withEncryptionAccess={withEncryptionAccess}
      />
    );
  }

  if (admissionsProfile.relationship_to_patient) {
    return (
      <BodyWrapper>
        <b className="-mr-3 font-bold">{`${translations.careproviderApp.recareAdmissionsForm.contactPersonInformation.sectionRelationship}${translations.general.colon} `}</b>
        <StringValue
          testId="relationship_to_patient"
          value={getOntology({
            key: admissionsProfile.relationship_to_patient,
            type: ONTOLOGIES.RELATIONSHIP_TO_PATIENT,
          })}
        />
      </BodyWrapper>
    );
  }

  return null;
};

function MainContact({
  auction,
  prefix,
}: {
  auction: Auction;
  prefix: string;
}) {
  const { isSealdOnly, withEncryptionAccess } = useEncryption();

  const { firstName, fullName, lastName } =
    getAdmissionsMainContactValue(auction);

  return (
    <EncryptedField
      isBold
      isSealdOnly={isSealdOnly}
      prefix={prefix}
      testId="admissions_main_contact_full_name"
      // combining two encrypted fields into one
      // needed for the encrypted field component to show its various states (blurred, decrypted, missing)
      value={{
        content: (firstName?.content || lastName?.content) ?? "",
        decrypted: fullName,
        iv: (firstName?.iv || lastName?.iv) ?? "",
        seald_content:
          (firstName?.seald_content || lastName?.seald_content) ?? "",
      }}
      withEncryptionAccess={withEncryptionAccess}
    />
  );
}

function useGetBackgroundColor({
  isProviderSearchRequest,
  isRequestAvailable,
}: {
  isProviderSearchRequest: boolean;
  isRequestAvailable: boolean;
}) {
  const theme = useTheme();
  if (isRequestAvailable) {
    return isProviderSearchRequest
      ? BRAND_PRIMARY_MAIN_COLOR
      : theme.palette.primary.main;
  } else {
    return undefined;
  }
}

export function getDirectRequestLabel({
  requestOrigin,
  state,
  translations,
}: {
  requestOrigin: AuctionRequestOrigin | undefined;
  translations: Translations;
  state: ProviderSearch | undefined;
}) {
  switch (requestOrigin) {
    case AUCTION_REQUEST_ORIGIN.ADMISSIONS_DIRECT:
      return translations.careproviderApp.recareAdmissionsProfile
        .recareAdmissionsPatient;
    case AUCTION_REQUEST_ORIGIN.ADMISSIONS_CAREPROVIDER:
      return translations.careproviderApp.recareAdmissionsProfile
        .manuallyAddedPatient;
    case AUCTION_REQUEST_ORIGIN.PROVIDER_SEARCH: {
      switch (state) {
        case PROVIDER_SEARCH_WESER_EMS:
          return `${translations.careproviderApp.dashboard.referrerDirect} ${translations.auctionRequest.weserEmsName}`;
        case PROVIDER_SEARCH_BAVARIA:
          return `${translations.careproviderApp.dashboard.referrerDirect} ${translations.auctionRequest.bcpName}`;
        default:
          return `${translations.careproviderApp.dashboard.referrerDirect}`;
      }
    }
    default:
      return null;
  }
}

function useGetDirectRequestLabel(
  requestOrigin: AuctionRequestOrigin | undefined,
) {
  const translations = useTranslations();
  const loggedCareprovider = useLoggedCareprovider();

  return getDirectRequestLabel({
    requestOrigin,
    state: loggedCareprovider?.provider_search_info?.state,
    translations,
  });
}

function AuctionRequestInfoCard({
  auction,
  auctionRequest,
  children,
  isRequestAvailable,
}: {
  auction: Auction;
  auctionRequest: AuctionRequest;
  children: ReactNode;
  isRequestAvailable: boolean;
}) {
  const translations = useTranslations();
  const directRequestLabel = useGetDirectRequestLabel(
    auctionRequest.request_origin,
  );
  const { isProviderSearchRequest } = getRequestOrigin(auctionRequest);

  const backgroundColor = useGetBackgroundColor({
    isProviderSearchRequest,
    isRequestAvailable,
  });

  return (
    <Card margin={margin(0, 0, 2, 0)}>
      <PaperHeader
        backgroundColor={backgroundColor}
        flexWrap
        padding={padding(1, 3)}
      >
        <VerticalLayout margin={margin(isProviderSearchRequest ? 0 : 1, 0)}>
          <Title
            bold
            light={!!isRequestAvailable}
            margin={isProviderSearchRequest ? margin(0, 2, 1.5) : margin(0, 2)}
            style={
              isProviderSearchRequest ? { fontWeight: FONT_WEIGHT_BOLD } : {}
            }
          >
            {isProviderSearchRequest
              ? translations.auctionRequest.bcpProfileName
              : translations.patient.patient}{" "}
            {auction?.patient?.user_id || ""}
          </Title>
          {directRequestLabel && (
            <HorizontalLayout
              aligned
              data-testid="direct-request-header"
              margin={margin(1, 2, 0)}
            >
              <CircleArrowRightIcon
                size={20}
                style={{ color: !isRequestAvailable ? BLACK : WHITE }}
              />
              <Body
                color={!isRequestAvailable ? BLACK : WHITE}
                fontSize={FONT_SIZE_14}
                fontWeight={FONT_WEIGHT_MEDIUM}
                margin={margin(0, 1)}
              >
                {directRequestLabel}
              </Body>
            </HorizontalLayout>
          )}
        </VerticalLayout>
        <AuctionRequestAssigneePanel
          auctionRequest={auctionRequest}
          isRequestAvailable={isRequestAvailable}
        />
      </PaperHeader>
      {children}
    </Card>
  );
}

function HospitalContact({
  auction,
  careseeker,
  social_worker,
  station,
}: {
  auction: Auction;
  careseeker: CareseekerType | undefined;
  social_worker: SocialWorkerType | undefined;
  station: StationType | null | undefined;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();

  const hospitalStay = auction.patient.diagnosis?.hospital_stay;

  return (
    <>
      <Subheading bold>
        {translations.careproviderProfile.clinicContactInformation({
          careseekerName: careseeker?.name || "",
        })}
      </Subheading>
      <ResponsiveLayout>
        {social_worker && (
          <CategoryContainer testId="social_worker">
            <StringField
              prefix={translations.careproviderProfile.contactPersonInHospital}
              testId="contact_person_in_hospital"
              value={getName(social_worker, getOntology)}
            />
            <StringField
              prefix={translations.people.tel}
              testId="social_worker_phone"
              value={formatPhoneNumber(social_worker.phone)}
            />
            <StringField
              prefix={translations.people.mobile}
              testId="social_worker_mobile_phone"
              value={formatPhoneNumber(social_worker.mobile_phone)}
            />
            {social_worker.email && (
              <Body display="flex">
                <span className="p-[2px] whitespace-nowrap">
                  {translations.people.mailShort}
                </span>
                <Link
                  href={`mailto:${social_worker.email}`}
                  primary
                  style={{ wordBreak: "break-all" }}
                >
                  {social_worker.email}
                </Link>
              </Body>
            )}
          </CategoryContainer>
        )}
        <CategoryContainer testId="hospital_contact">
          <StringField
            prefix={translations.patient.station}
            testId="station_name"
            value={
              station?.name
                ? concatStrings(station.name, station.description)
                : null
            }
          />
          <StringField
            prefix={translations.patient.stationPhoneNumber}
            testId="station_phone_number"
            value={station?.phone_number}
          />
          <EmptiableField
            hasNoEmptyValue
            testId="doctor_in_charge_in_hospital"
            title={translations.patient.doctorInCharge}
            value={hospitalStay?.doctor_in_charge_in_hospital}
          />
          {!isCareSearch(auction.search_type) && (
            <EmptiableField
              hasNoEmptyValue
              testId="doctor_in_charge_in_hospital_phone"
              title={translations.people.tel}
              value={hospitalStay?.doctor_in_charge_in_hospital_phone}
            />
          )}
        </CategoryContainer>
      </ResponsiveLayout>
    </>
  );
}

function ConnextBanner({ auctionRequest }: { auctionRequest: AuctionRequest }) {
  const translations = useTranslations();
  const hasConnextExtension = useHasCareproviderExtension(
    EXTENSIONS[EXTENSION_CONNEXT],
  );
  const { last_integration_event, status } = auctionRequest;
  const isAttributed = status === AUCTION_REQUEST_ATTRIBUTED;

  if (
    !hasConnextExtension ||
    !hasConnextExtension.connected ||
    !last_integration_event?.event_type
  ) {
    return null;
  }

  if (
    last_integration_event.event_type ===
      INTEGRATION_EVENT_TYPES.SEARCH_ATTRIBUTED &&
    isAttributed
  ) {
    return (
      <Banner
        color="success"
        message={
          translations.careproviderApp.settings.extensions.connext.exportSuccess
        }
      />
    );
  }

  if (last_integration_event.event_type !== null && !isAttributed) {
    return (
      <Banner
        color="warning"
        message={translations.careproviderApp.settings.extensions.exportRevert}
      />
    );
  }

  return null;
}

export const AdmissionsRequestInfoCard = ({
  auction,
  auctionRequest,
}: {
  auction: Auction;
  auctionRequest: AuctionRequest;
}) => {
  const translations = useTranslations();
  const isPrint = isPrintMode();
  const { isSealdOnly, withEncryptionAccess } = useEncryption();
  const { isAdmissionsRequest } = getRequestOrigin(auctionRequest);

  if (!isAdmissionsRequest) {
    return null;
  }

  return (
    <>
      <Divider />
      <VerticalLayout
        gap={margin(1, 0)}
        padding={isPrint ? padding(1, 0) : padding(1, 3)}
      >
        <Subheading bold fullWidth margin={margin(0, 2)}>
          {
            translations.careproviderApp.recareAdmissionsProfile
              .headerContactDetails
          }
        </Subheading>
        <MainContact
          auction={auction}
          prefix={
            translations.careproviderApp.recareAdmissionsForm
              .contactPersonInformation.title
          }
        />
        <RelationshipDisplay admissionsProfile={auction.admissions?.profile} />
        <EncryptedField
          isBold
          isSealdOnly={isSealdOnly}
          prefix={
            translations.careproviderApp.recareAdmissionsForm
              .contactPersonInformation.phoneNumber
          }
          testId="admissions_main_contact_phone"
          value={auction.admissions?.profile?.main_contact_phone}
          withEncryptionAccess={withEncryptionAccess}
        />
      </VerticalLayout>
    </>
  );
};

export function OverviewCard({
  auction,
  auctionRequest,
  isRequestAvailable,
}: {
  auction: Auction;
  auctionRequest: AuctionRequest | null | undefined;
  isRequestAvailable: boolean | null | undefined;
}) {
  const locale = useLocale();
  const getOntology = useGetOntology();
  const translations = useTranslations();

  if (!auctionRequest?.solutions?.length) {
    return null;
  }

  const { isDirectRequest } = getRequestOrigin(auctionRequest);
  const hasSocialWorkerAndCareseeker =
    !!auction?.patient?.social_worker && !!auction?.patient.careseeker;

  return (
    <AuctionRequestInfoCard
      auction={auction}
      auctionRequest={auctionRequest}
      isRequestAvailable={!!isRequestAvailable}
    >
      <PreferredProviderBanner auctionRequest={auctionRequest} />
      <ConnextBanner auctionRequest={auctionRequest} />
      <CardContentContainer noPadding>
        {!isRequestAvailable && (
          <Banner
            color="primary"
            message={translations.auctionRequest.patientNoLongerAvailable}
          />
        )}
        <VerticalLayout minHeight={dp(84)}>
          <StaticChipInfo
            auction={auction}
            auctionRequest={auctionRequest}
            getOntology={getOntology}
            locale={locale}
            requestAvailable={isRequestAvailable}
            translations={translations}
          />
        </VerticalLayout>
        {!isDirectRequest && (
          <>
            <Divider />
            <VerticalLayout padding={padding(1, 3)}>
              {hasSocialWorkerAndCareseeker && (
                <HospitalContact
                  auction={auction}
                  careseeker={auction.patient.careseeker}
                  social_worker={auction.patient.social_worker}
                  station={auction?.patient.station_full}
                />
              )}
              <TransportInfoCard auction={auction} isForCareprovider />
            </VerticalLayout>
          </>
        )}
        <AdmissionsRequestInfoCard
          auction={auction}
          auctionRequest={auctionRequest}
        />
      </CardContentContainer>
    </AuctionRequestInfoCard>
  );
}
